(() => {
  if (customElements.get('rn-quick-add-selection')) return;

  class QuickAddSelection extends HTMLElement {
    connectedCallback() {
      this.choices = new Map();
      this.pending = false;
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onCarouselClick = this.onCarouselClick.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.addEventListener('change', this.onChange, true);
      this.addEventListener('submit', this.onSubmit, true);
      this.addEventListener('click', this.onSubmit, true);
      this.addEventListener('click', this.onCarouselClick);
      this.addEventListener('touchstart', this.onTouchStart, {passive: true});
      this.addEventListener('touchend', this.onTouchEnd, {passive: true});
      queueMicrotask(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected) return;
      this.info = this.querySelector('product-info');
      this.variants = JSON.parse(this.querySelector('[data-rn-variants]')?.textContent || '[]');
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this.info, {childList: true, subtree: true});
      this.refresh();
      this.updateMediaCount();
    }

    get picker() {
      return [...this.querySelectorAll('variant-selects')].find(node => node.style.display !== 'none');
    }

    get groups() {
      return [...(this.picker?.querySelectorAll('[data-option-position]') || [])];
    }

    get selection() {
      return this.groups.map(group => this.choices.get(group.dataset.optionPosition));
    }

    get selectedVariant() {
      const choices = this.selection;
      if (!choices.length || choices.some(value => !value)) return null;
      return this.variants?.find(variant => variant.options.every((value, index) => value === choices[index].value));
    }

    onChange(event) {
      const input = event.target.closest('input[data-rn-option-position]');
      if (!input || !this.contains(input)) return;
      event.stopImmediatePropagation();
      this.choices.set(input.dataset.rnOptionPosition, {id: input.dataset.optionValueId, value: input.value});
      this.error = false;
      this.pending = false;
      clearTimeout(this.requestTimer);
      this.info.abortController?.abort();
      this.querySelectorAll('variant-selects').forEach(node => {
        if (node.style.display === 'none') node.remove();
      });
      this.refresh();
      const variant = this.selectedVariant;
      if (!variant?.available) return;

      if (variant.mediaId) {
        this.selectMediaById(variant.mediaId);
      }

      this.pending = true;
      this.requestPicker = this.picker;
      this.refresh();
      this.info.handleOptionValueChange({data: {
        event,
        target: input,
        selectedOptionValues: this.selection.map(choice => choice.id),
      }});
      this.requestTimer = setTimeout(() => {
        if (!this.pending || !this.isConnected) return;
        this.info.abortController?.abort();
        this.pending = false;
        this.error = true;
        this.refresh();
      }, 15000);
    }

    refresh() {
      if (!this.info || this.updating) return;
      this.updating = true;
      let candidates = (this.variants || []).filter(variant => variant.available);
      for (const group of this.groups) {
        const position = group.dataset.optionPosition;
        const index = Number(position) - 1;
        let choice = this.choices.get(position);
        if (choice && !candidates.some(variant => variant.options[index] === choice.value)) {
          this.choices.delete(position);
          choice = null;
        }
        for (const input of group.querySelectorAll('input[data-rn-option-position]')) {
          const available = candidates.some(variant => variant.options[index] === input.value);
          input.disabled = !available;
          input.classList.toggle('disabled', !available);
          input.checked = choice?.id === input.dataset.optionValueId;
          input.setAttribute('aria-disabled', String(!available));
        }
        const label = group.querySelector('[data-selected-value]');
        if (label && label.textContent.trim() !== (choice?.value || '')) label.textContent = choice?.value || '';
        if (choice) candidates = candidates.filter(variant => variant.options[index] === choice.value);
      }

      const variant = this.selectedVariant;
      const rendered = JSON.parse(this.picker?.querySelector('[data-selected-variant]')?.textContent || 'null');
      if (this.pending && this.picker !== this.requestPicker && rendered?.id === variant?.id) {
        this.pending = false;
        clearTimeout(this.requestTimer);
      }
      const serverUnavailable = rendered?.id === variant?.id && (rendered?.available === false || this.info.submitButton?.disabled === true);
      this.ready = Boolean(variant?.available && rendered?.available && rendered.id === variant.id && !serverUnavailable && !this.pending && !this.error);
      const button = this.info.querySelector('[data-add-to-cart]');
      const variantInput = this.info.querySelector('product-form input[name="id"]');
      if (button) button.disabled = !this.ready;
      if (variantInput) {
        variantInput.disabled = !this.ready;
        if (!this.ready) variantInput.value = '';
        else variantInput.value = String(variant.id);
      }
      const status = this.querySelector('[data-rn-selection-status]');
      const message = this.error ? this.dataset.error : this.pending ? this.dataset.loading : serverUnavailable ? this.dataset.unavailable : this.ready ? this.dataset.ready : this.dataset.prompt;
      if (status && status.textContent !== message) status.textContent = message;
      this.updating = false;
    }

    onSubmit(event) {
      const isSubmit = event.type === 'submit' || event.target.closest('[data-add-to-cart]');
      if (!isSubmit || this.ready) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const missing = this.groups.find(group => !this.choices.has(group.dataset.optionPosition));
      missing?.querySelector('input:not(:disabled)')?.focus();
    }

    get mediaSlides() {
      return [...this.querySelectorAll('.product-quick-add__images [data-slide]')];
    }

    get activeMediaIndex() {
      const index = this.mediaSlides.findIndex(slide => slide.classList.contains('media--active'));
      return index < 0 ? 0 : index;
    }

    selectMedia(index) {
      const slides = this.mediaSlides;
      if (slides.length < 2) return;
      const nextIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.classList.toggle('media--active', active);
        slide.classList.toggle('media--hidden', !active);
        slide.classList.remove('media--hiding');
      });
      const selected = slides[nextIndex];
      const mediaRoot = selected.closest('product-images');
      if (mediaRoot && selected.dataset.mediaId) mediaRoot.dataset.activeMedia = selected.dataset.mediaId;
      selected.dispatchEvent(new CustomEvent('theme:media:select', {bubbles: true, detail: {id: selected.dataset.mediaId}}));
      this.updateMediaCount(nextIndex);
    }

    selectMediaById(mediaId) {
      const index = this.mediaSlides.findIndex(slide => String(slide.dataset.imageId) === String(mediaId));
      if (index >= 0) this.selectMedia(index);
    }

    updateMediaCount(index = this.activeMediaIndex) {
      const counter = this.querySelector('[data-rn-media-current]');
      if (counter) counter.textContent = String(index + 1);
    }

    onCarouselClick(event) {
      if (event.target.closest('[data-rn-media-prev]')) this.selectMedia(this.activeMediaIndex - 1);
      if (event.target.closest('[data-rn-media-next]')) this.selectMedia(this.activeMediaIndex + 1);
    }

    onTouchStart(event) {
      if (!event.target.closest('.product-quick-add__images')) return;
      this.touchStartX = event.changedTouches[0]?.clientX;
    }

    onTouchEnd(event) {
      if (!event.target.closest('.product-quick-add__images') || this.touchStartX == null) return;
      const distance = event.changedTouches[0]?.clientX - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(distance) < 45) return;
      this.selectMedia(this.activeMediaIndex + (distance < 0 ? 1 : -1));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      clearTimeout(this.requestTimer);
      this.removeEventListener('change', this.onChange, true);
      this.removeEventListener('submit', this.onSubmit, true);
      this.removeEventListener('click', this.onSubmit, true);
      this.removeEventListener('click', this.onCarouselClick);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchend', this.onTouchEnd);
    }
  }

  customElements.define('rn-quick-add-selection', QuickAddSelection);
})();
